import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import ReactDOM from 'react-dom/client';
import React from 'react';
import { PilotData } from '../types';
import PilotPopup from '../components/PilotPopup';

interface UsePilotPopupOptions {
  map: maplibregl.Map | null;
  pilots: Map<string, PilotData>;
  markers: Map<string, maplibregl.Marker>;
  onLoadHistory?: (pilotId: string) => void;
  onToggleHistory?: (pilotId: string, show: boolean) => void;
  trackManager?: any; // TrackManager instance
  onCenterPilot?: (pilot: PilotData) => void;
  onFollowPilot?: (pilotId: string | null) => void;
  followingPilot?: string | null;
}

export const usePilotPopup = ({
  map,
  pilots,
  markers,
  onLoadHistory,
  onToggleHistory,
  trackManager,
  onCenterPilot,
  onFollowPilot,
  followingPilot
}: UsePilotPopupOptions) => {
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const popupRootRef = useRef<ReactDOM.Root | null>(null);
  const currentPilotIdRef = useRef<string | null>(null);
  const attachedHandlersRef = useRef<Set<string>>(new Set());

  // Update pilots ref when pilots change
  const pilotsRef = useRef<Map<string, PilotData>>(pilots);
  useEffect(() => {
    pilotsRef.current = pilots;
  }, [pilots]);

  // Set up click handlers and map handler once
  useEffect(() => {
    if (!map) return;

    // Add map click handler for closing popup
    const handleMapClick = (e: any) => {
      // Check if clicking on popup itself
      const target = e.originalEvent?.target as HTMLElement;
      if (target?.closest('.maplibregl-popup')) {
        return; // Don't close if clicking on popup
      }

      // Check if clicking on a marker
      if (target?.closest('.pilot-marker')) {
        return; // Don't close, let marker handler deal with it
      }

      // Close popup for any other clicks
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
        currentPilotIdRef.current = null;
      }
    };

    // Add map click handler
    map.on('click', handleMapClick);

    // Clean up on unmount
    return () => {
      // Remove map click handler
      map.off('click', handleMapClick);

      // Clean up popup if exists
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      const rootToUnmount = popupRootRef.current;
      popupRootRef.current = null;
      if (rootToUnmount) {
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    };
  }, [map]); // Only re-run when map changes

  // Handle marker click handlers
  useEffect(() => {
    if (!map) return;
    if (markers.size === 0) {
      return;
    }

    // Remove handlers for pilots that no longer exist
    const currentPilotIds = new Set(Array.from(markers.keys()));
    attachedHandlersRef.current.forEach(pilotId => {
      if (!currentPilotIds.has(pilotId)) {
        attachedHandlersRef.current.delete(pilotId);
      }
    });

    // Add click handlers for all markers
    markers.forEach((marker, pilotId) => {
      // Skip if already attached
      if (attachedHandlersRef.current.has(pilotId)) {
        return;
      }

      const element = marker.getElement();
      if (!element) {
        return;
      }

      const clickHandler = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();

          const pilot = pilotsRef.current.get(pilotId);
          if (!pilot) return;

          // Close existing popup if clicking different pilot
          if (currentPilotIdRef.current !== pilotId && popupRef.current) {
            popupRef.current.remove();
            const rootToUnmount = popupRootRef.current;
            popupRootRef.current = null;
            if (rootToUnmount) {
              rootToUnmount.unmount();
            }
          }

          // Don't reopen if same pilot
          if (currentPilotIdRef.current === pilotId && popupRef.current) {
            return;
          }

          currentPilotIdRef.current = pilotId;

          // Create popup
          const popupContainer = document.createElement('div');
          const root = ReactDOM.createRoot(popupContainer);

          // Check if history is loaded for this pilot
          const track = trackManager?.getTrack?.(pilotId);
          const isHistoryLoading = track?.isHistoryLoading || false;
          const hasHistory = track?.isHistoryLoaded || false;
          const isShowingHistory = track?.showingHistory || false;

          root.render(
            <PilotPopup
              pilot={pilot}
              onLoadHistory={onLoadHistory}
              onToggleHistory={onToggleHistory}
              isHistoryLoading={isHistoryLoading}
              hasHistory={hasHistory}
              isShowingHistory={isShowingHistory}
              onCenter={onCenterPilot}
              onFollow={onFollowPilot}
              isFollowing={followingPilot === pilotId}
            />
          );
          popupRootRef.current = root;

          const popup = new maplibregl.Popup({
            className: 'premium-popup compact-popup',
            closeButton: true,
            closeOnClick: false,
            closeOnMove: false,
            maxWidth: '280px',
            offset: [0, -20],  // Offset to position just above the paraglider icon
            anchor: 'bottom'
          })
            .setDOMContent(popupContainer)
            .setLngLat([pilot.lng, pilot.lat])
            .addTo(map);

          popupRef.current = popup;

          popup.on('close', () => {
            currentPilotIdRef.current = null;
            const rootToUnmount = popupRootRef.current;
            popupRootRef.current = null;
            if (rootToUnmount) {
              setTimeout(() => {
                rootToUnmount.unmount();
              }, 0);
            }
          });
        };

      // Mark as attached BEFORE adding listener to prevent race conditions
      attachedHandlersRef.current.add(pilotId);

      // Add click listener in capture phase
      element.addEventListener('click', clickHandler, true);
      element.setAttribute('data-pilot-id', pilotId);
    });
  }, [map, markers, onLoadHistory, onToggleHistory, trackManager, onCenterPilot, onFollowPilot, followingPilot]); // Re-run when markers change

  // Update popup when track changes (for loading state)
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  useEffect(() => {
    // Force update every 500ms while loading to check track state
    const interval = setInterval(() => {
      if (currentPilotIdRef.current && trackManager) {
        const track = trackManager.getTrack?.(currentPilotIdRef.current);
        if (track?.isHistoryLoading) {
          forceUpdate();
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [trackManager]);

  // Update popup content and position if pilot data changes while popup is open
  useEffect(() => {
    if (!popupRef.current) return;

    const root = popupRootRef.current;
    if (!root) return;

    // Get the pilot ID of the open popup
    const pilotId = currentPilotIdRef.current;
    if (!pilotId) return;

    // Find the current pilot data
    const currentPilot = pilots.get(pilotId);

    // Update popup content if pilot found
    if (currentPilot) {
      try {
        // Check if history is loaded for this pilot
        const track = trackManager?.getTrack?.(currentPilot.id);
        const isHistoryLoading = track?.isHistoryLoading || false;
        const hasHistory = track?.isHistoryLoaded || false;
        const isShowingHistory = track?.showingHistory || false;

        console.log(`🔄 Popup update - Loading: ${isHistoryLoading}, HasHistory: ${hasHistory}, Showing: ${isShowingHistory}`);

        root.render(
          <PilotPopup
            pilot={currentPilot}
            onLoadHistory={onLoadHistory}
            onToggleHistory={onToggleHistory}
            isHistoryLoading={isHistoryLoading}
            hasHistory={hasHistory}
            isShowingHistory={isShowingHistory}
            onCenter={onCenterPilot}
            onFollow={onFollowPilot}
            isFollowing={followingPilot === currentPilot.id}
          />
        );
        // Update popup position in case pilot moved
        popupRef.current.setLngLat([currentPilot.lng, currentPilot.lat]);
      } catch (error) {
        // Root might have been unmounted, ignore the error
        // Popup update skipped - root unmounted
      }
    }
  }, [pilots, onLoadHistory, onToggleHistory, trackManager, onCenterPilot, onFollowPilot, followingPilot]);

  return {
    closePopup: () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      const rootToUnmount = popupRootRef.current;
      popupRootRef.current = null;
      if (rootToUnmount) {
        // Defer unmounting to avoid React warning
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    }
  };
};